import { Drawer } from 'antd';
import { CSSProperties, Key, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { selectVisibleSettings, setVisibleSettings } from '../../../features/analysisConfig/sessionInfoSlice';
import { FeatureFlag, UserClaimsWithTSDB } from '../../../types/userType';
import { WithFeatureFlagsCheck } from '../../../with_feature_flags_check';
import AggregatePeptidesSettings from './AggregatePeptidesSettings';
import ChemicalCalibrationSettings from './ChemicalCalibrationSettings';
import HumidityCompensationSettings from './HumidityCompensationSettings';
import PeptidesSelectorSettings from './PeptidesSelectorSettings';
import SubstractItemSettings from './SubstractItemSettings';
import ThresholdSignatureSettings from './ThresholdSignatureSettings';
import ThresholdIntensitySettings from './ThresholdIntensitySettings';
import InterpretationResultSettings from './InterpretationResultSettings';
import { fetchAuthorizedAPIEndpoint } from '../../../utils';
import { selectSessionID } from '../../../features/analysisConfig/analysisConfigSlice';
import { RunMetadata } from '../../../types/runsType';
import SaveAnalysisConfig from './SaveAnalysisConfig';
import { useAuth0 } from '@auth0/auth0-react';

type DashboardSettingsProps = {};

type typeStyle = {
  [K in Key]: CSSProperties;
};

const DashboardSettings: React.FC<DashboardSettingsProps> = () => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const [accessToken, setAccessToken] = useState<string | undefined>();
  const sessionID = useAppSelector(selectSessionID);
  const visibleSettings = useAppSelector(selectVisibleSettings);
  const [activeComparisonSession, setActiveComparisonSession] = useState<boolean>(true);
  const [userInfo, setUserInfo] = useState<UserClaimsWithTSDB | null>(null);

  const dispatch = useAppDispatch();
  const style: typeStyle = {
    container: { position: 'relative', height: '100vh', width: visibleSettings ? 400 : 0, padding: visibleSettings ? 48 : 0, overflow: 'hidden', textAlign: 'center' },
  };

  useEffect(() => {
    getAccessTokenSilently().then((token) => {
      setAccessToken(token);
    });
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!user || !isAuthenticated) setUserInfo(null);
    else setUserInfo(user as UserClaimsWithTSDB);
  }, [isAuthenticated, user]);

  // Load runs
  useEffect(() => {
    if (!accessToken) {
      return;
    }
    if (sessionID !== '') {
      fetchAuthorizedAPIEndpoint(`/runs?session_id=${sessionID}`, accessToken)
        .then((resp) => {
          if (resp.ok) {
            return resp.json();
          } else {
            throw new Error();
          }
        })
        .then((runs: RunMetadata[]) => {
          if (runs != null) {
            setActiveComparisonSession(
              runs
                .map((run) => run.Tags)
                .flat()
                .includes('$comparison-analysis')
            );
          }
        });
    }
  }, [accessToken, sessionID]);

  return (
    <div style={style.container}>
      <Drawer
        title="Dashboard settings"
        placement="right"
        open={visibleSettings}
        getContainer={false}
        styles={{ body: { overflowY: 'scroll', textAlign: 'left', paddingBottom: 60, marginBottom: 50 }, header: { borderBottom: 'none' } }}
        style={{ position: 'fixed', right: 0, top: 50, width: style.container.width, height: 'calc(100% - 50px)' }}
        mask={false}
        onClose={() => dispatch(setVisibleSettings(false))}
      >
        <WithFeatureFlagsCheck ff={FeatureFlag.AANonAggregatedPeptidesAccessEnabled}>
          <AggregatePeptidesSettings userInfo={userInfo} />
        </WithFeatureFlagsCheck>
        <HumidityCompensationSettings userInfo={userInfo} />
        <WithFeatureFlagsCheck ff={FeatureFlag.AAChemicalCalibrationAccessEnabled}>
          <ChemicalCalibrationSettings />
        </WithFeatureFlagsCheck>
        <PeptidesSelectorSettings />
        <SubstractItemSettings userInfo={userInfo} />
        {activeComparisonSession && (
          <>
            <ThresholdSignatureSettings userInfo={userInfo} />
            <ThresholdIntensitySettings userInfo={userInfo} />
            <InterpretationResultSettings userInfo={userInfo} />
            {userInfo && !userInfo.feature_flags.includes(FeatureFlag.AALightViewQualityControl) && <SaveAnalysisConfig />}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default DashboardSettings;
